/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onBeforeUnmount,
  onMounted,
  Teleport,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  trainingTutorials,
  canViewArticle,
  getTrainingTutorial,
  isTutorialInAppEnvironment,
  localizeText,
  type AppEnvironment,
  type TrainingTutorial,
} from "@lemmonhq/knowledgebase";
import { useLocalization } from "@lemmonhq/uikit";
import { useTraining } from "@app/vue/providers/Training";

const sectionForPath = (path: string): string | null => {
  if (path.includes("/manager/reservations") || path.includes("/reservations"))
    return "reservations";
  if (path.includes("/tables")) return "tables";
  if (path.includes("/orders")) return "orders";
  if (path.includes("/pos")) return "pos";
  if (path.includes("/catalog_editor") || path.includes("/products"))
    return "catalog";
  if (path.includes("/reports")) return "reports";
  if (path.includes("/loyalty")) return "loyalty";
  if (path.includes("/settings")) return "settings";
  return null;
};

const tutorialSection = (tutorial: TrainingTutorial): string =>
  tutorial.id.split(".")[0] || "";

// Build-stamped by vite define; the typeof guard covers a hot-updated module on a
// dev server started before the define existed.
const helpTooltip = `Help and Tutorials (v${
  typeof __HELP_BUILD_VERSION__ === "string" ? __HELP_BUILD_VERSION__ : "0.0.0-dev"
})`;

// Staging/local count as "development"; only an explicit production build is "production".
const currentAppEnvironment: AppEnvironment =
  import.meta.env.VITE_ENV === "production" ? "production" : "development";

// apps/web users are the "guest" knowledge-base role. A tutorial shows here only when
// both its role visibility (canViewArticle) and environment visibility permit it.
const isTutorialAvailableInApp = (tutorial: TrainingTutorial): boolean =>
  canViewArticle(tutorial.visibility, "guest") &&
  isTutorialInAppEnvironment(tutorial, currentAppEnvironment);

export const HelpMenu = defineComponent({
  name: "HelpMenu",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const training = useTraining();
    const { locale } = useLocalization();
    const open = ref(false);
    const containerRef = ref<HTMLElement | null>(null);
    const triggerRef = ref<HTMLElement | null>(null);
    const panelRef = ref<HTMLElement | null>(null);
    const sharedId = ref<string | null>(null);
    let sharedTimer: number | null = null;

    // The navbar is `position: sticky`, which always creates a stacking context.
    // A dropdown nested inside it therefore cannot paint above page content that
    // comes later in the document — on /app/pos the menu ended up *underneath*
    // the catalog search field and swallowed the click. Teleporting the panel to
    // <body> takes it out of the navbar's stacking context; the trade-off is that
    // it must be positioned against the viewport by hand.
    const panelPos = ref({ top: 0, right: 0 });
    // Teleporting also escapes the `data-theme` wrapper, so daisyUI would resolve
    // bg-base-100 and friends against the default theme. Carry the theme across.
    const themeName = ref<string | null>(null);

    const syncPanelPos = () => {
      const el = triggerRef.value;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      panelPos.value = {
        top: Math.round(rect.bottom + 4),
        right: Math.round(window.innerWidth - rect.right),
      };
      themeName.value =
        el.closest("[data-theme]")?.getAttribute("data-theme") ?? null;
    };

    const section = computed(() => sectionForPath(route.path));

    const sectionTutorials = computed<TrainingTutorial[]>(() => {
      const s = section.value;
      if (!s) return [];
      return trainingTutorials.filter(
        (t) => tutorialSection(t) === s && isTutorialAvailableInApp(t),
      );
    });

    const localeKey = computed(() => {
      const l = locale.value || "en";
      return (l.split("-")[0].split("_")[0] || "en") as
        | "en"
        | "de"
        | "fr"
        | "it"
        | "ka";
    });

    const onDocClick = (event: MouseEvent) => {
      if (!open.value) return;
      const target = event.target as Node;
      const el = containerRef.value;
      if (el && el.contains(target)) return;
      // The panel lives under <body> now, so it is not inside containerRef.
      const panel = panelRef.value;
      if (panel && panel.contains(target)) return;
      open.value = false;
    };

    // A teleported panel does not follow the trigger, so re-anchor it whenever the
    // page scrolls (capture: true also catches scrolls in nested containers) or
    // the viewport resizes.
    const onReflow = () => {
      if (open.value) syncPanelPos();
    };

    onMounted(() => {
      window.addEventListener("click", onDocClick);
      window.addEventListener("scroll", onReflow, true);
      window.addEventListener("resize", onReflow);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("click", onDocClick);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    });

    const startTutorial = async (id: string) => {
      open.value = false;
      const tutorial = getTrainingTutorial(id);
      const targetRoute = tutorial?.route;
      if (targetRoute && route.path !== targetRoute) {
        await router.push(targetRoute);
        window.setTimeout(() => training.start(id, { force: true }), 500);
      } else {
        training.start(id, { force: true });
      }
    };

    const startAll = () => {
      const first = sectionTutorials.value[0];
      if (!first) return;
      startTutorial(first.id);
    };

    const shareTutorial = async (tutorial: TrainingTutorial) => {
      const path = tutorial.route ?? route.path;
      const url = `${window.location.origin}${path}?tutorial=${encodeURIComponent(tutorial.id)}`;
      const title = localizeText(tutorial.title, localeKey.value);
      const text = localizeText(tutorial.summary, localeKey.value);

      const flashCopied = () => {
        sharedId.value = tutorial.id;
        if (sharedTimer !== null) window.clearTimeout(sharedTimer);
        sharedTimer = window.setTimeout(() => {
          sharedId.value = null;
          sharedTimer = null;
        }, 1500);
      };

      const nav = navigator as Navigator & {
        share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
      };
      if (typeof nav.share === "function") {
        try {
          await nav.share({ title, text, url });
          return;
        } catch {
          // user cancelled or share failed — fall through to clipboard
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        flashCopied();
      } catch {
        window.prompt("Copy this link:", url);
      }
    };

    onBeforeUnmount(() => {
      if (sharedTimer !== null) window.clearTimeout(sharedTimer);
    });

    return () => {
      const tutorials = sectionTutorials.value;
      const hasAny = tutorials.length > 0;

      // No tutorials for this view → hide the help button entirely.
      if (!hasAny) return null;

      // "Start all" is redundant when there's only a single tutorial.
      const showStartAll = tutorials.length > 1;

      return (
        <div
          ref={(el) => (containerRef.value = el as HTMLElement | null)}
          class="relative inline-flex items-center"
        >
          <button
            ref={(el) => (triggerRef.value = el as HTMLElement | null)}
            type="button"
            class="inline-flex items-center justify-center w-9 h-9 rounded-full text-base-content/70 hover:text-base-content hover:bg-base-200 transition-colors"
            aria-label={helpTooltip}
            title={helpTooltip}
            aria-haspopup="menu"
            aria-expanded={open.value ? "true" : "false"}
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              const next = !open.value;
              if (next) syncPanelPos();
              open.value = next;
            }}
          >
            <span class="material-symbols-outlined text-2xl">help</span>
          </button>

          {open.value ? (
            <Teleport to="body">
              <div
                ref={(el) => (panelRef.value = el as HTMLElement | null)}
                data-theme={themeName.value ?? undefined}
                class="fixed w-72 rounded-lg bg-base-100 shadow-2xl ring-1 ring-base-content/10 z-[9999] overflow-hidden"
                style={`top: ${panelPos.value.top}px; right: ${panelPos.value.right}px;`}
                role="menu"
              >
              {showStartAll ? (
                <button
                  type="button"
                  role="menuitem"
                  class="w-full text-left px-4 py-3 hover:bg-base-200 transition-colors flex items-start gap-2"
                  onClick={startAll}
                >
                  <span class="material-symbols-outlined text-xl text-primary mt-0.5">
                    play_circle
                  </span>
                  <span class="flex flex-col">
                    <span class="text-sm font-semibold text-base-content">
                      Start all tutorials
                    </span>
                    <span class="text-xs text-base-content/60">
                      Walk through every tutorial for this view.
                    </span>
                  </span>
                </button>
              ) : null}

              {hasAny ? (
                <>
                  {showStartAll ? (
                    <div class="border-t border-base-content/10" />
                  ) : null}
                  <div class="max-h-80 overflow-y-auto">
                    {tutorials.map((tutorial) => {
                      const justShared = sharedId.value === tutorial.id;
                      return (
                        <div
                          key={tutorial.id}
                          role="menuitem"
                          class="group flex items-start gap-1 hover:bg-base-200 transition-colors"
                        >
                          <button
                            type="button"
                            class="flex-1 min-w-0 text-left pl-4 pr-1 py-2.5 flex items-start gap-2"
                            onClick={() => startTutorial(tutorial.id)}
                          >
                            <span class="material-symbols-outlined text-base text-base-content/55 mt-0.5">
                              school
                            </span>
                            <span class="flex flex-col min-w-0">
                              <span class="text-sm font-medium text-base-content truncate">
                                {localizeText(tutorial.title, localeKey.value)}
                              </span>
                              <span class="text-xs text-base-content/60 line-clamp-2">
                                {localizeText(tutorial.summary, localeKey.value)}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            class="shrink-0 mr-2 mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full text-base-content/55 hover:text-base-content hover:bg-base-300 transition-colors"
                            aria-label={justShared ? "Link copied" : "Share tutorial"}
                            title={justShared ? "Link copied" : "Share tutorial"}
                            onClick={(event: MouseEvent) => {
                              event.stopPropagation();
                              shareTutorial(tutorial);
                            }}
                          >
                            <span class="material-symbols-outlined text-base">
                              {justShared ? "check" : "ios_share"}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
              </div>
            </Teleport>
          ) : null}
        </div>
      );
    };
  },
});
